import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  CircularProgress
} from "@mui/material";

import { useTesterIdentity } from "./TesterIdentityProvider";
import { askCodeQuestion } from "./api";

const SENSITIVE_KEY_PATTERN = /token|secret|password|apikey|api_key|auth/i;

const captureClientContext = () => {
  try {
    return Object.fromEntries(
      Object.entries(sessionStorage).filter(
        ([key]) =>
          !SENSITIVE_KEY_PATTERN.test(key) &&
          !key.startsWith("firebase:") &&
          !key.startsWith("__")
      )
    );
  } catch {
    return {};
  }
};

const AskQuestionDialog = ({ open, onClose, onAsked }) => {
  const { getIdToken } = useTesterIdentity();
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setQuestion("");
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!question.trim()) {
      setError("Question is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await askCodeQuestion({
        question: question.trim(),
        pageUrl: window.location.href,
        clientContext: captureClientContext(),
        getIdToken
      });
      reset();
      onAsked?.(result.questionId);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to ask");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Ask about the code</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Ask anything about how the platform works. The agent will read the
            relevant code and report back in ~2–3 min. Examples: "How is the
            CSV export implemented?", "Does the Customers tab handle archived
            companies?"
          </Typography>
          <TextField
            label="Your question"
            multiline
            minRows={4}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={submitting}
            autoFocus
            fullWidth
          />
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !question.trim()}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting ? "Asking…" : "Ask"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AskQuestionDialog;
